/**
 * Campaigns, the Advocacy plan add-on (design section 11, node vvOmk; feature `campaigns`):
 *   /dashboard/campaigns              All campaigns (Bro2c): tiles, All · Running · Drafts · Ended, the table with Last 14 days.
 *   /dashboard/campaigns/<id>         One campaign (hBM1o): tiles, Targets with forecasts, the message, Action page, Supporters by day.
 *   /dashboard/campaigns/<id>/page    Action page (q3e7Uo): Content · Design · Sharing, with the phone / desktop preview.
 * Paints from snapshot:campaigns / snapshot:campaign (worker/src/campaigns.js); refetches on the team's OrgRoom
 * `campaign.*` deltas. Not in the design, kept minimal: New campaign, Edit targets, the Content and Sharing tabs, the
 * Start / Resume buttons and the Reply picker.
 */
import { Dropdown } from "../components/shared/Dropdown";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  ArrowLeft, ArrowRight, Check, ChevronDown, CircleCheck, Copy, ExternalLink, GripVertical, Monitor, MoveHorizontal, Pause, Play, Plus, Send,
  Smartphone, TrendingDown, TrendingUp, Upload, X,
} from "lucide-react";
import { TabBar } from "../components/shared/ui";
import { List, ListRow, RowCell, RowLead, RowMain, RowSub, RowTitle } from "../components/shared/List";
import { navigate, plainClick } from "../app/router";
import { toastStore } from "../stores/toastStore";
import { errorText } from "../lib/apiError";
import { useBusy } from "../hooks/useBusy";
import { useFocusTrap } from "../hooks/useFocusTrap";
import {
  FONTS, LOGO_MAX_BYTES, PRESETS, PRESET_NAMES, buttonText, contrastLine, fontsHref, initials, itemLine, previewBody, shortDate, statusLabel,
  type PageDesign, type PageTheme,
} from "./model";
import type { CampaignListRow, CampaignSnapshot, CampaignsSnapshot, Member, TargetRow } from "./types";
import "../casework/casework.css";
import "./campaigns.css";

const API = "/api/dashboard/campaigns";
const inlined = <T,>(id: string): T | null => {
  const el = typeof document !== "undefined" ? document.getElementById(id) : null;
  try { return el?.textContent ? (JSON.parse(el.textContent) as T) : null; } catch { return null; }
};
async function call<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const r = await fetch(`${API}${path}`, { method: init.method ?? "GET", credentials: "include",
    headers: init.body === undefined ? undefined : { "content-type": "application/json" }, body: init.body === undefined ? undefined : JSON.stringify(init.body) });
  const d = await r.json().catch(() => null);
  if (!r.ok) throw new Error(errorText(d) || "Something went wrong");
  return d as T;
}
const failed = (e: unknown) => toastStore.getState().show({ message: (e as Error).message || "Something went wrong" });
const fmt = (n: number) => n.toLocaleString("en-US");
const go = (path: string) => (e: React.MouseEvent) => { if (!plainClick(e)) return; e.preventDefault(); navigate(path); };
/** Refetch when the team's OrgRoom says a campaign changed (overview/live.ts dispatches team:work). */
function useLive(reload: () => void) {
  useEffect(() => {
    const on = (e: Event) => { if (String((e as CustomEvent<{ t?: string }>).detail?.t).startsWith("campaign.")) reload(); };
    addEventListener("team:work", on);
    return () => removeEventListener("team:work", on);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}

export function CampaignsPage({ path }: { path: string }) {
  const m = path.match(/^\/dashboard\/campaigns(?:\/([^/]+)(\/page)?)?\/?$/);
  const id = m?.[1] ? decodeURIComponent(m[1]) : null;
  if (!id) return <CampaignList />;
  return <CampaignRoute key={id} id={id} page={!!m?.[2]} />;
}

// ---------- All campaigns

type ListTab = "all" | "running" | "drafts" | "ended";
const PAGE = 20;

function CampaignList() {
  const [snap, setSnap] = useState<CampaignsSnapshot | null>(() => inlined<CampaignsSnapshot>("snapshot:campaigns"));
  const [tab, setTab] = useState<ListTab>("all");
  const [all, setAll] = useState(false);
  const [creating, setCreating] = useState(false);
  const reload = () => void call<CampaignsSnapshot>("").then(setSnap).catch(() => {});
  useEffect(() => { if (!snap) reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useLive(reload);
  if (!snap) return null;
  const s = snap.stats;
  const inTab = (c: CampaignListRow) => tab === "all" || (tab === "running" ? c.status === "running" || c.status === "paused" : tab === "drafts" ? c.status === "draft" : c.status === "ended");
  const count = (t: ListTab) => snap.campaigns.filter((c) => t === "all" || (t === "running" ? c.status === "running" || c.status === "paused" : t === "drafts" ? c.status === "draft" : c.status === "ended")).length;
  const rows = snap.campaigns.filter(inTab);
  const shown = all ? rows : rows.slice(0, PAGE);
  const change = s.previousMonth.n > 0 ? Math.round(((s.supportersThisMonth - s.previousMonth.n) / s.previousMonth.n) * 100) : null;
  const member = (id: string | null) => snap.members.find((x) => x.id === id) ?? null;
  return (
    <div className="cp">
      <header className="cw-head">
        <h1 className="cp-title">Campaigns</h1>
        <button className="cw-btn cw-btn--primary" onClick={() => setCreating(true)}><Plus size={14} aria-hidden />New campaign</button>
      </header>
      <div className="cw-tiles">
        <Tile label="Running" value={s.running} />
        <Tile label="Supporters this month" value={s.supportersThisMonth} sub={change !== null ? `${change >= 0 ? "+" : "−"}${Math.abs(change)}% from ${s.previousMonth.month}` : null} />
        <Tile label="Emails to alderpersons" value={s.emails} />
        <Tile label="Replies from offices" value={s.replies} sub={s.meetings ? `${s.meetings} asked for a meeting` : null} />
      </div>
      <TabBar label="Campaigns" active={tab} onChange={(t) => { setTab(t); setAll(false); }} tabs={[
        { id: "all", label: `All · ${count("all")}` }, { id: "running", label: `Running · ${count("running")}` },
        { id: "drafts", label: `Drafts · ${count("drafts")}` }, { id: "ended", label: `Ended · ${count("ended")}` }]} />
      <div className="cp-table" role="table" aria-label="Campaigns">
        <List as="div" className="cp-list">
          <ListRow as="div" role="row" className="cp-row cp-row--head ls-head">
            <RowMain min={120}><span role="columnheader">Campaign</span></RowMain>
            <RowCell width={150}><span role="columnheader">Status</span></RowCell>
            <RowCell width={96}><span role="columnheader">Supporters</span></RowCell>
            <RowCell width={80} hide="phone"><span role="columnheader">Emails</span></RowCell>
            <RowCell width={120} hide="phone"><span role="columnheader">Toss-ups reached</span></RowCell>
            <RowCell width={130} hide="phone"><span role="columnheader">Last 14 days</span></RowCell>
            <RowCell width={56}><span role="columnheader">Owner</span></RowCell>
          </ListRow>
          {shown.map((c) => {
            const st = statusLabel(c);
            const draft = c.status === "draft";
            const href = `/dashboard/campaigns/${encodeURIComponent(c.id)}`;
            const owner = member(c.ownerId);
            return (
              <ListRow key={c.id} as="div" className="cp-row" role="row" href={href}>
                <RowMain min={120}><span role="cell" className="cw-cell">
                  <RowTitle className="cw-case__t cp-name">{c.name}</RowTitle><RowSub className="cw-case__s">{itemLine(c)}</RowSub>
                </span></RowMain>
                <RowCell width={150} className="cp-c-status"><span role="cell"><span className="cp-chip" data-tone={st.tone}>{st.label}</span></span></RowCell>
                <RowCell width={96}><span role="cell" className="cp-num" data-empty={draft || undefined}>{draft ? "—" : fmt(c.supporters)}</span></RowCell>
                <RowCell width={80} hide="phone"><span role="cell" className="cp-num" data-empty={draft || undefined}>{draft ? "—" : fmt(c.emails)}</span></RowCell>
                <RowCell width={120} hide="phone"><span role="cell" className="cp-num" data-empty={draft || !c.targets || undefined}>{draft || !c.targets ? "—" : `${c.targets.reached} of ${c.targets.total}`}</span></RowCell>
                <RowCell width={130} hide="phone" className="cp-c-spark"><span role="cell"><Spark counts={c.status === "running" || c.status === "paused" ? c.last14 : []} /></span></RowCell>
                <RowCell width={56}><span role="cell"><span className="cw-avatar" title={owner?.name} aria-label={owner?.name}>{owner ? initials(owner.name) : "·"}</span></span></RowCell>
              </ListRow>
            );
          })}
        </List>
        {rows.length > shown.length && <button className="cp-more" onClick={() => setAll(true)}>{`Showing ${shown.length} of ${rows.length}`}</button>}
      </div>
      {creating && <NewCampaign onClose={() => setCreating(false)} />}
    </div>
  );
}

function Tile({ label, value, sub }: { label: string; value: string | number; sub?: string | null }) {
  return (
    <div className="cw-tile">
      <span className="cw-tile__l">{label}</span>
      <span className="cw-tile__v">{typeof value === "number" ? fmt(value) : value}</span>
      {sub && <span className="cw-tile__s">{sub}</span>}
    </div>
  );
}

/** Last 14 days: one bar per day, the last three darker; a flat line where there were none. */
function Spark({ counts }: { counts: number[] }) {
  const max = Math.max(0, ...counts);
  if (!max) return <span className="cp-spark cp-spark--flat" aria-hidden><span /></span>;
  return (
    <span className="cp-spark" role="img" aria-label={counts.join(", ")}>
      {counts.map((n, i) => <span key={i} data-recent={i >= counts.length - 3 || undefined} data-zero={!n || undefined} style={{ height: n ? `${Math.max(4, Math.round((n / max) * 24))}px` : undefined }} />)}
    </span>
  );
}

function Dialog({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  const box = useRef<HTMLDivElement>(null);
  useFocusTrap(box, true, { onEscape: onClose });
  return (
    <div className="dialog-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog cw-dialog--wide" role="dialog" aria-modal="true" aria-labelledby="cp-dialog-title" ref={box}>
        <header className="dialog-head">
          <h2 id="cp-dialog-title">{title}</h2>
          <button className="icon-btn" aria-label="Close" onClick={onClose}><X size={16} /></button>
        </header>
        {children}
      </div>
    </div>
  );
}

function NewCampaign({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState("");
  const { busy, run } = useBusy();
  return (
    <Dialog title="New campaign" onClose={onClose}>
      <form className="cw-form" onSubmit={(e) => { e.preventDefault(); void run(async () => {
        try { const d = await call<CampaignSnapshot>("", { method: "POST", body: { name } }); onClose(); navigate(`/dashboard/campaigns/${encodeURIComponent(d.campaign.id)}/page`); } catch (err) { failed(err); }
      }); }}>
        <label className="field-label" htmlFor="cp-new-name">Campaign</label>
        <input id="cp-new-name" className="text-input" autoFocus value={name} onChange={(e) => setName(e.target.value)} />
        <div className="dialog-actions"><button className="cw-btn cw-btn--primary" disabled={busy || !name.trim()} aria-busy={busy || undefined}><Plus size={14} aria-hidden />New campaign</button></div>
      </form>
    </Dialog>
  );
}

// ---------- One campaign

function CampaignRoute({ id, page }: { id: string; page: boolean }) {
  const [snap, setSnap] = useState<CampaignSnapshot | null>(() => { const s = inlined<CampaignSnapshot>("snapshot:campaign"); return s?.campaign.id === id ? s : null; });
  const reload = () => void call<CampaignSnapshot>(`/${encodeURIComponent(id)}`).then(setSnap).catch(() => {});
  useEffect(() => { if (!snap) reload(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useLive(reload);
  if (!snap) return null;
  return page ? <ActionPageEditor snap={snap} onChange={setSnap} /> : <CampaignDetail snap={snap} onChange={setSnap} />;
}

const patch = (id: string, body: object) => call<CampaignSnapshot>(`/${encodeURIComponent(id)}`, { method: "PATCH", body });

function CampaignDetail({ snap, onChange }: { snap: CampaignSnapshot; onChange: (s: CampaignSnapshot) => void }) {
  const c = snap.campaign, s = snap.stats;
  const st = statusLabel(c);
  const { busy, run } = useBusy();
  const [editing, setEditing] = useState(false);
  const save = (body: object) => run(async () => { try { onChange(await patch(c.id, body)); } catch (e) { failed(e); } });
  const pageHref = `/dashboard/campaigns/${encodeURIComponent(c.id)}/page`;
  return (
    <div className="cp">
      <a className="cp-back" href="/dashboard/campaigns" onClick={go("/dashboard/campaigns")}><ArrowLeft size={14} aria-hidden />Campaigns</a>
      <header className="cw-head cp-head">
        <div className="cp-head__t">
          <div className="cp-head__row"><span className="cp-chip" data-tone={st.tone}>{st.label}</span><span className="cw-case__s cp-item">{itemLine(c)}</span></div>
          <h1 className="cp-title">{c.name}</h1>
        </div>
        <div className="cw-actions">
          <a className="cw-btn" href={snap.url} target="_blank" rel="noopener"><ExternalLink size={14} aria-hidden />Open action page</a>
          {c.status === "running" && <button className="cw-btn" disabled={busy} onClick={() => void save({ status: "paused" })}><Pause size={14} aria-hidden />Pause</button>}
          {c.status === "paused" && <button className="cw-btn" disabled={busy} onClick={() => void save({ status: "running" })}><Play size={14} aria-hidden />Resume</button>}
          {c.status === "draft" && <button className="cw-btn cw-btn--primary" disabled={busy} onClick={() => void save({ status: "running" })}><Play size={14} aria-hidden />Start</button>}
        </div>
      </header>
      <div className="cw-tiles">
        <Tile label="Supporters who acted" value={s.supporters} sub={s.sinceMonday ? `+${fmt(s.sinceMonday)} since Monday` : null} />
        <Tile label="Emails to alderpersons" value={s.emails} />
        <Tile label="Toss-ups reached" value={s.targets ? `${s.reached} of ${s.targets}` : "—"} sub={s.noSupporters ? `${s.noSupporters} ${s.noSupporters === 1 ? "has" : "have"} no supporters yet` : null} />
        <Tile label="Replies from offices" value={s.replies} sub={s.meetings ? `${s.meetings} asked for a meeting` : null} />
      </div>
      <div className="cw-cols">
        <div className="cp-main">
          <section className="cp-targets" aria-labelledby="cp-targets-h">
            <div className="cp-sec-head">
              <h2 id="cp-targets-h" className="cp-h2">Targets</h2>
              <button className="cw-link" onClick={() => setEditing(true)}>Edit targets<ArrowRight size={14} aria-hidden /></button>
            </div>
            <div role="table" aria-labelledby="cp-targets-h">
              <List as="div" className="cp-tlist">
                <ListRow as="div" role="row" className="cp-trow cp-trow--head ls-head">
                  <RowMain min={120}><span role="columnheader">Alderperson</span></RowMain>
                  <RowCell width={150} hide="phone"><span role="columnheader">Forecast</span></RowCell>
                  <RowCell width={90}><span role="columnheader">Supporters</span></RowCell>
                  <RowCell width={70}><span role="columnheader">Emails</span></RowCell>
                  <RowCell width={90} hide="phone"><span role="columnheader">Reply</span></RowCell>
                </ListRow>
                {snap.targets.map((t) => <Target key={t.ward} t={t} onReply={(reply) => void run(async () => {
                  try { onChange(await call<CampaignSnapshot>(`/${encodeURIComponent(c.id)}/targets/${t.ward}`, { method: "PUT", body: { reply } })); } catch (e) { failed(e); }
                })} />)}
              </List>
            </div>
          </section>
          <Message snap={snap} onChange={onChange} />
        </div>
        <aside className="cp-side">
          <section className="cp-card" aria-labelledby="cp-ap-h">
            <h2 id="cp-ap-h" className="cp-h3">Action page</h2>
            <UrlBox url={snap.url} />
            <div className="cp-channels">
              <button className="cw-chip cp-chip-btn" onClick={() => void copy(snap.url)}>Link</button>
              <a className="cw-chip cp-chip-btn" href={`${pageHref}?tab=sharing`} onClick={go(`${pageHref}?tab=sharing`)}>QR code</a>
              <a className="cw-chip cp-chip-btn" href={`${pageHref}?tab=sharing`} onClick={go(`${pageHref}?tab=sharing`)}>Embed</a>
            </div>
            <a className="cw-link" href={`${pageHref}?tab=design`} onClick={go(`${pageHref}?tab=design`)}>Edit design<ArrowRight size={14} aria-hidden /></a>
          </section>
          <section className="cp-card" aria-labelledby="cp-days-h">
            <h2 id="cp-days-h" className="cp-h3">Supporters by day</h2>
            <Days counts={snap.byDay.counts} from={snap.byDay.from} />
          </section>
        </aside>
      </div>
      {editing && <EditTargets snap={snap} onClose={() => setEditing(false)} onChange={onChange} />}
    </div>
  );
}

const copy = async (text: string) => {
  try { await navigator.clipboard.writeText(text); toastStore.getState().show({ message: "Link copied" }); } catch { /* no clipboard */ }
};

function UrlBox({ url }: { url: string }) {
  return (
    <div className="cp-url">
      <span className="cw-mono cp-url__v">{url.replace(/^https?:\/\//, "")}</span>
      <button className="cp-url__copy" aria-label="Copy link" onClick={() => void copy(url)}><Copy size={14} aria-hidden /></button>
    </div>
  );
}

const FORECAST_ICON = { likely: TrendingUp, tossup: MoveHorizontal, unlikely: TrendingDown } as const;
const FORECAST_LABEL = { likely: "Likely", tossup: "Toss-up", unlikely: "Unlikely" } as const;
const REPLY_LABEL = { meeting: "Asked to meet", form: "Form letter", replied: "Replied" } as const;

function Target({ t, onReply }: { t: TargetRow; onReply: (r: TargetRow["reply"]) => void }) {
  const I = t.forecast ? FORECAST_ICON[t.forecast.label] : null;
  return (
    <ListRow as="div" className="cp-trow" role="row">
      <RowMain min={120}><span role="cell" className="cw-cell"><RowTitle className="cw-case__t">{t.name}</RowTitle><RowSub className="cw-case__s">Ward {t.ward}</RowSub></span></RowMain>
      <RowCell width={150} hide="phone"><span role="cell">{t.forecast && I
        ? <span className="cp-fc" data-label={t.forecast.label}><I size={12} aria-hidden />{`${FORECAST_LABEL[t.forecast.label]} · ${t.forecast.p}%`}</span>
        : <span className="cp-num" data-empty>—</span>}</span></RowCell>
      <RowCell width={90}><span role="cell" className="cp-num" data-zero={t.supporters === 0 || undefined}>{fmt(t.supporters)}</span></RowCell>
      <RowCell width={70}><span role="cell" className="cp-num">{fmt(t.emails)}</span></RowCell>
      <RowCell width={90} hide="phone"><span role="cell">
        <Dropdown className="cp-reply" data={{ "data-reply": t.reply ?? undefined }} chevron={false} label={`Reply from Ward ${t.ward}`}
          value={t.reply ?? ""} onChange={(v) => onReply((v || null) as TargetRow["reply"])}
          options={[{ value: "", label: "—" }, ...(Object.keys(REPLY_LABEL) as (keyof typeof REPLY_LABEL)[]).map((k) => ({ value: k as string, label: REPLY_LABEL[k] }))]} />
      </span></RowCell>
    </ListRow>
  );
}

function Days({ counts, from }: { counts: number[]; from: string }) {
  const max = Math.max(1, ...counts);
  return (
    <div className="cp-days">
      <div className="cp-days__bars" role="img" aria-label={counts.join(", ")}>
        {counts.map((n, i) => <span key={i} data-recent={i >= counts.length - 3 || undefined} style={{ height: `${Math.max(2, Math.round((n / max) * 88))}px` }} />)}
      </div>
      <div className="cp-days__axis"><span>{shortDate(from)}</span><span>Today</span></div>
    </div>
  );
}

function Message({ snap, onChange }: { snap: CampaignSnapshot; onChange: (s: CampaignSnapshot) => void }) {
  const c = snap.campaign;
  const [subject, setSubject] = useState(c.subject);
  const [body, setBody] = useState(c.body);
  const save = (b: object) => void patch(c.id, b).then(onChange).catch(failed);
  return (
    <section className="cp-card cp-message" aria-labelledby="cp-msg-h">
      <div className="cp-sec-head">
        <h2 id="cp-msg-h" className="cp-h2">Message supporters send</h2>
        <span className="cp-edit">Supporters can edit
          <button className="cp-switch" role="switch" aria-checked={c.supportersCanEdit} aria-label="Supporters can edit" onClick={() => save({ supportersCanEdit: !c.supportersCanEdit })}><span /></button>
        </span>
      </div>
      <label className="cp-field"><span className="cp-field__l">Subject</span>
        <input className="cp-input" value={subject} onChange={(e) => setSubject(e.target.value)} onBlur={() => subject !== c.subject && save({ subject })} /></label>
      <label className="cp-field"><span className="cp-field__l">Body</span>
        <textarea className="cp-input cp-body" value={body} onChange={(e) => setBody(e.target.value)} onBlur={() => body !== c.body && save({ body })} /></label>
    </section>
  );
}

function EditTargets({ snap, onClose, onChange }: { snap: CampaignSnapshot; onClose: () => void; onChange: (s: CampaignSnapshot) => void }) {
  const [wards, setWards] = useState(() => new Set(snap.targets.map((t) => t.ward)));
  const { busy, run } = useBusy();
  return (
    <Dialog title="Edit targets" onClose={onClose}>
      <List as="div" className="cp-pick">
        {snap.alderpersons.map((a) => (
          <ListRow key={a.ward} as="label" className="cp-pick__row">
            <RowLead><input type="checkbox" checked={wards.has(a.ward)} onChange={(e) => { const n = new Set(wards); if (e.target.checked) n.add(a.ward); else n.delete(a.ward); setWards(n); }} /></RowLead>
            <RowMain min={120}><RowTitle className="cw-case__t">{a.name}</RowTitle><RowSub className="cw-case__s">Ward {a.ward}</RowSub></RowMain>
          </ListRow>
        ))}
      </List>
      <div className="dialog-actions">
        <button className="cw-btn cw-btn--primary" disabled={busy} aria-busy={busy || undefined} onClick={() => void run(async () => {
          try { onChange(await call<CampaignSnapshot>(`/${encodeURIComponent(snap.campaign.id)}/targets`, { method: "PUT", body: { wards: [...wards] } })); onClose(); } catch (e) { failed(e); }
        })}><Check size={14} aria-hidden />Edit targets</button>
      </div>
    </Dialog>
  );
}

// ---------- Action page: Content · Design · Sharing

type PageTab = "content" | "design" | "sharing";

function ActionPageEditor({ snap, onChange }: { snap: CampaignSnapshot; onChange: (s: CampaignSnapshot) => void }) {
  const c = snap.campaign;
  const [tab, setTab] = useState<PageTab>(() => {
    const t = typeof location !== "undefined" ? new URLSearchParams(location.search).get("tab") : null;
    return t === "content" || t === "sharing" ? t : "design";
  });
  const [design, setDesign] = useState<PageDesign>(c.design);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const { busy, run } = useBusy();
  const edit = (d: PageDesign) => {
    setDesign(d);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => void patch(c.id, { design: d }).then(onChange).catch(failed), 600);
  };
  const back = `/dashboard/campaigns/${encodeURIComponent(c.id)}`;
  return (
    <div className="cp">
      <a className="cp-back" href={back} onClick={go(back)}><ArrowLeft size={14} aria-hidden />{c.name}</a>
      <header className="cw-head">
        <h1 className="cp-title">Action page</h1>
        <div className="cw-actions">
          <a className="cw-btn" href={snap.url} target="_blank" rel="noopener"><ExternalLink size={14} aria-hidden />Open page</a>
          <button className="cw-btn cw-btn--primary" disabled={busy} aria-busy={busy || undefined} onClick={() => void run(async () => {
            clearTimeout(timer.current);
            try { await patch(c.id, { design }); onChange(await call<CampaignSnapshot>(`/${encodeURIComponent(c.id)}/publish`, { method: "POST" })); } catch (e) { failed(e); }
          })}><Check size={14} aria-hidden />Publish changes</button>
        </div>
      </header>
      <TabBar label="Action page" active={tab} onChange={(t) => { setTab(t); navigate(`${location.pathname}?tab=${t}`, { replace: true }); }}
        tabs={[{ id: "content", label: "Content" }, { id: "design", label: "Design" }, { id: "sharing", label: "Sharing" }]} />
      <div className="cp-editor">
        <div className="cp-editor__form">
          {tab === "design" && <DesignForm d={design} onChange={edit} />}
          {tab === "content" && <ContentForm snap={snap} onChange={onChange} />}
          {tab === "sharing" && <SharingForm snap={snap} onChange={onChange} />}
        </div>
        <Preview snap={snap} d={design} />
      </div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return <section className="cp-card cp-card--ed"><h2 className="cp-h4">{title}</h2>{children}</section>;
}

function DesignForm({ d, onChange }: { d: PageDesign; onChange: (d: PageDesign) => void }) {
  const file = useRef<HTMLInputElement>(null);
  const theme = (t: Partial<PageTheme>) => onChange({ ...d, theme: { ...d.theme, ...t } });
  const ratio = contrastLine(d.theme);
  const upload = (f: File | undefined) => {
    if (!f) return;
    if (f.size > LOGO_MAX_BYTES || !/^image\/(svg\+xml|png|jpeg|webp)$/.test(f.type)) { failed(new Error("Use an SVG, PNG, JPEG or WebP under 200 KB.")); return; }
    const r = new FileReader();
    r.onload = () => onChange({ ...d, logo: { name: f.name, size: f.size, dataUrl: String(r.result) } });
    r.readAsDataURL(f);
  };
  const links = d.footer.links;
  const setLinks = (l: PageDesign["footer"]["links"]) => onChange({ ...d, footer: { ...d.footer, links: l } });
  return (
    <>
      <Card title="Logo">
        <div className="cp-logo">
          <span className="cp-logo__tile">{d.logo && <img src={d.logo.dataUrl} alt="" />}</span>
          {d.logo && <span className="cw-case"><span className="cw-case__t">{d.logo.name}</span><span className="cw-case__s">{`${Math.max(1, Math.round(d.logo.size / 1024))} KB`}</span></span>}
        </div>
        <div className="cw-actions">
          <button className="cw-btn" onClick={() => file.current?.click()}><Upload size={14} aria-hidden />Replace</button>
          {d.logo && <button className="cw-btn" onClick={() => onChange({ ...d, logo: null })}>Remove</button>}
          <input ref={file} type="file" accept="image/svg+xml,image/png,image/jpeg,image/webp" hidden onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
      </Card>
      <Card title="Theme">
        <div className="cp-presets" role="radiogroup" aria-label="Theme">
          {PRESET_NAMES.map((n) => {
            const p = PRESETS[n]!;
            return (
              <button key={n} role="radio" aria-checked={d.theme.preset === n} className="cp-preset" onClick={() => onChange({ ...d, theme: { preset: n, ...p } })}>
                <span className="cp-preset__sw" style={{ background: p.bg }}><span style={{ background: p.accent }} /></span>
                <span className="cp-preset__n">{n}</span>
              </button>
            );
          })}
        </div>
        {([["bg", "Background"], ["text", "Text"], ["accent", "Buttons and links"], ["card", "Cards"]] as const).map(([k, label]) => (
          <div key={k} className="cp-color">
            <label className="cp-color__sw" style={{ background: d.theme[k] }}>
              <input type="color" aria-label={label} value={d.theme[k].toLowerCase()} onChange={(e) => theme({ [k]: e.target.value.toUpperCase(), preset: "Custom" })} />
            </label>
            <span className="cp-color__n">{label}</span>
            <HexInput value={d.theme[k]} label={label} onChange={(v) => theme({ [k]: v, preset: "Custom" })} />
          </div>
        ))}
        <div className="cp-contrast" data-ok={ratio.ok || undefined}><CircleCheck size={14} aria-hidden />{`Text ${ratio.text.toFixed(1)}:1 · Button text ${ratio.button.toFixed(1)}:1`}</div>
      </Card>
      <Card title="Fonts">
        {(["heading", "body"] as const).map((k) => (
          <label key={k} className="cp-field"><span className="cp-field__l">{k === "heading" ? "Headings" : "Body"}</span>
            <span className="cp-select">
              <span className="cp-select__aa" style={{ fontFamily: `'${d.fonts[k]}', var(--font)`, fontWeight: k === "heading" ? 600 : 400 }}>Aa</span>
              <Dropdown className="cp-select__dd" chevron={<ChevronDown size={14} aria-hidden />} label={k === "heading" ? "Headings" : "Body"}
                value={d.fonts[k]} onChange={(v) => onChange({ ...d, fonts: { ...d.fonts, [k]: v } })} options={FONTS.map((f) => ({ value: f as string, label: f }))} />
            </span>
          </label>
        ))}
      </Card>
      <Card title="Footer">
        <label className="cp-field"><span className="cp-field__l">Text</span>
          <textarea className="cp-input cp-footer-text" value={d.footer.text} onChange={(e) => onChange({ ...d, footer: { ...d.footer, text: e.target.value } })} /></label>
        <div className="cp-field"><span className="cp-field__l">Links</span>
          {links.map((l, i) => (
            <div key={i} className="cp-linkrow">
              <GripVertical size={14} aria-hidden className="cp-grip" />
              <input className="cp-input cp-linkrow__l" aria-label="Label" value={l.label} onChange={(e) => setLinks(links.map((x, k) => (k === i ? { ...x, label: e.target.value } : x)))} />
              <input className="cp-input cp-linkrow__u" aria-label="URL" value={l.url} onChange={(e) => setLinks(links.map((x, k) => (k === i ? { ...x, url: e.target.value } : x)))} />
              <button className="icon-btn" aria-label={`Remove ${l.label}`} onClick={() => setLinks(links.filter((_, k) => k !== i))}><X size={14} /></button>
            </div>
          ))}
          {links.length < 6 && <button className="cp-add" onClick={() => setLinks([...links, { label: "", url: "https://" }])}><Plus size={14} aria-hidden />Add link</button>}
        </div>
      </Card>
    </>
  );
}

function HexInput({ value, label, onChange }: { value: string; label: string; onChange: (v: string) => void }) {
  const [v, setV] = useState(value);
  useEffect(() => setV(value), [value]);
  return <input className="cp-hex cw-mono" aria-label={`${label} hex`} value={v} onChange={(e) => { setV(e.target.value); if (/^#[0-9a-f]{6}$/i.test(e.target.value)) onChange(e.target.value.toUpperCase()); }} />;
}

function ContentForm({ snap, onChange }: { snap: CampaignSnapshot; onChange: (s: CampaignSnapshot) => void }) {
  const c = snap.campaign;
  const save = (b: object) => void patch(c.id, b).then(onChange).catch(failed);
  const text = (k: "name" | "item" | "recordNumber" | "eyebrow" | "headline", label: string) => (
    <label className="cp-field"><span className="cp-field__l">{label}</span>
      <input className="cp-input" defaultValue={c[k] ?? ""} onBlur={(e) => e.target.value !== (c[k] ?? "") && save({ [k]: e.target.value })} /></label>
  );
  return (
    <Card title="Content">
      {text("name", "Campaign")}
      {text("eyebrow", "Eyebrow")}
      {text("headline", "Headline")}
      {text("recordNumber", "Record number")}
      {text("item", "Item")}
      <label className="cp-field"><span className="cp-field__l">Vote date</span>
        <input className="cp-input" type="date" defaultValue={c.voteDate ?? ""} onBlur={(e) => e.target.value !== (c.voteDate ?? "") && save({ voteDate: e.target.value || null })} /></label>
      <label className="cp-field"><span className="cp-field__l">Position</span>
        <Dropdown label="Position" value={c.position ?? ""} onChange={(v) => save({ position: v || null })}
          options={[{ value: "", label: "—" }, { value: "support", label: "Support" }, { value: "oppose", label: "Oppose" }]} /></label>
      <label className="cp-field"><span className="cp-field__l">Send to</span>
        <Dropdown label="Send to" value={c.targetMode as string} onChange={(v) => save({ targetMode: v })}
          options={[{ value: "own", label: "Each supporter’s own alderperson" }, { value: "list", label: "Targets" }]} /></label>
      <label className="cp-field"><span className="cp-field__l">Status</span>
        <Dropdown label="Status" value={c.status as string} onChange={(v) => save({ status: v })}
          options={[{ value: "draft", label: "Draft" }, { value: "running", label: "Running" }, { value: "paused", label: "Paused" }, { value: "ended", label: "Ended" }]} /></label>
      {c.status === "ended" && (
        <label className="cp-field"><span className="cp-field__l">Outcome</span>
          <Dropdown label="Outcome" value={c.outcome ?? ""} onChange={(v) => save({ outcome: v || null })}
            options={[{ value: "", label: "—" }, { value: "passed", label: "passed" }, { value: "held", label: "held" }, { value: "failed", label: "failed" }]} /></label>
      )}
    </Card>
  );
}

function SharingForm({ snap, onChange }: { snap: CampaignSnapshot; onChange: (s: CampaignSnapshot) => void }) {
  const c = snap.campaign;
  return (
    <Card title="Sharing">
      <UrlBox url={snap.url} />
      <label className="cp-field"><span className="cp-field__l">Link</span>
        <input className="cp-input cw-mono" defaultValue={c.slug} onBlur={(e) => e.target.value !== c.slug && void patch(c.id, { slug: e.target.value }).then(onChange).catch(failed)} /></label>
    </Card>
  );
}

/** The action page as supporters will see it (the public page is worker/src/campaign-act.js; same layout and tokens). */
function Preview({ snap, d }: { snap: CampaignSnapshot; d: PageDesign }) {
  const [device, setDevice] = useState<"phone" | "desktop">("phone");
  const c = snap.campaign;
  const t = d.theme;
  useEffect(() => {
    const id = "cp-preview-fonts";
    let link = document.getElementById(id) as HTMLLinkElement | null;
    if (!link) { link = document.createElement("link"); link.id = id; link.rel = "stylesheet"; document.head.appendChild(link); }
    link.href = fontsHref(d.fonts);
  }, [d.fonts.heading, d.fonts.body]); // eslint-disable-line react-hooks/exhaustive-deps
  // A sample alderperson for the preview: the first target, or the first ward.
  const sample: { ward: number; name: string } | null = snap.targets[0] ?? snap.alderpersons[0] ?? null;
  const vars = { "--ap-bg": t.bg, "--ap-text": t.text, "--ap-accent": t.accent, "--ap-card": t.card, "--ap-on-accent": buttonText(t),
    "--ap-head": `'${d.fonts.heading}', var(--font)`, "--ap-body": `'${d.fonts.body}', var(--font)` } as React.CSSProperties;
  return (
    <div className="cp-preview">
      <div className="cp-device" role="radiogroup" aria-label="Preview">
        <button role="radio" aria-checked={device === "phone"} onClick={() => setDevice("phone")}><Smartphone size={14} aria-hidden />Phone</button>
        <button role="radio" aria-checked={device === "desktop"} onClick={() => setDevice("desktop")}><Monitor size={14} aria-hidden />Desktop</button>
      </div>
      <div className="cp-frame" data-device={device}>
        <div className="ap" style={vars}>
          <div className="ap-top">{d.logo && <img src={d.logo.dataUrl} alt="" />}<span className="ap-org">{snap.orgName}</span></div>
          <div className="ap-heading">{c.eyebrow && <span className="ap-eyebrow">{c.eyebrow}</span>}<h3 className="ap-h1">{c.headline || c.name}</h3></div>
          <div className="ap-field"><span className="ap-l">Your address</span><span className="ap-input" /></div>
          {sample && (
            <div className="ap-ald"><span className="ap-avatar">{initials(sample.name)}</span><span className="ap-who"><b>{`Ald. ${sample.name}`}</b><span>{`Ward ${sample.ward}`}</span></span><CircleCheck size={22} aria-hidden className="ap-ok" /></div>
          )}
          <div className="ap-field"><span className="ap-l">Your message</span>
            <div className="ap-message"><span className="ap-subject">{c.subject}</span><span className="ap-body">{previewBody(c.body, { lastName: sample?.name.split(" ").at(-1) ?? "{last name}", ward: sample?.ward ?? "{ward}" })}</span></div></div>
          <div className="ap-field"><span className="ap-l">Why it matters to you (optional)</span><span className="ap-input ap-input--tall" /></div>
          <div className="ap-field"><span className="ap-l">Name</span><span className="ap-input" /></div>
          <div className="ap-field"><span className="ap-l">Email</span><span className="ap-input" /></div>
          <span className="ap-send"><Send size={18} aria-hidden />{sample ? `Send to Ald. ${sample.name.split(" ").at(-1)}` : ""}</span>
          {sample && <span className="ap-fine">{`Sent from your email to the Ward ${sample.ward} office.`}</span>}
          {(d.footer.text || d.footer.links.length > 0) && (
            <div className="ap-footer">{d.footer.text && <p>{d.footer.text}</p>}
              {d.footer.links.length > 0 && <nav>{d.footer.links.map((l, i) => <span key={i}>{l.label}</span>)}</nav>}</div>
          )}
        </div>
      </div>
    </div>
  );
}
